import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const menuItems = [
  { label: 'Ana Sayfa', section: 'home' },
  { label: 'Hizmetlerimiz', section: 'services' },
  { label: 'Hakkımızda', section: 'about' },
  { label: 'İletişim', section: 'contact' },
];

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const open = Boolean(anchorEl);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 88;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    handleClose();
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentSection = menuItems.find((item) => {
        const element = document.getElementById(item.section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 140 && rect.bottom >= 140;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.section);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: trigger ? 'rgba(5, 8, 22, 0.82)' : 'transparent',
        boxShadow: trigger ? '0 12px 32px rgba(2, 6, 23, 0.32)' : 'none',
        borderBottom: trigger
          ? '1px solid rgba(148, 163, 184, 0.12)'
          : '1px solid transparent',
        backdropFilter: trigger ? 'blur(18px)' : 'none',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box
            component="button"
            type="button"
            onClick={() => scrollToSection('home')}
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              bgcolor: 'transparent',
              border: 0,
              p: 0,
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src="/images/gnc-mark-512.png"
              alt=""
              aria-hidden="true"
              sx={{
                display: 'block',
                width: { xs: 42, sm: 48 },
                height: { xs: 42, sm: 48 },
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: '0 0 22px rgba(34, 211, 238, 0.2)',
              }}
            />
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'common.white',
                  fontFamily: '"General Sans", "Inter", sans-serif',
                  fontWeight: 800,
                  letterSpacing: 0,
                  lineHeight: 1,
                }}
              >
                GNC
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(248, 251, 255, 0.72)',
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  fontWeight: 600,
                  letterSpacing: 0,
                  lineHeight: 1.25,
                }}
              >
                Veri Teknolojileri
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {menuItems.map((item) => (
              <Button
                key={item.label}
                onClick={() => scrollToSection(item.section)}
                sx={{
                  color: 'rgba(248, 251, 255, 0.88)',
                  mx: 0.4,
                  px: 1.6,
                  opacity: activeSection === item.section ? 1 : 0.72,
                  borderRadius: 999,
                  bgcolor:
                    activeSection === item.section
                      ? 'rgba(255, 255, 255, 0.06)'
                      : 'transparent',
                  '&:hover': {
                    opacity: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => scrollToSection('contact')}
              sx={{
                color: '#06111f',
                background: 'linear-gradient(180deg, #ffffff 0%, #dff6ff 100%)',
              }}
            >
              Hızlı İletişim
            </Button>
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{
                color: 'white',
                border: '1px solid rgba(255,255,255,0.12)',
                bgcolor: 'rgba(255,255,255,0.04)',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiPaper-root': {
                  borderRadius: 4,
                  mt: 1.5,
                  boxShadow: '0 20px 50px rgba(2, 6, 23, 0.45)',
                  backgroundColor: 'rgba(9, 17, 31, 0.96)',
                  border: '1px solid rgba(148, 163, 184, 0.12)',
                  color: 'white',
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {menuItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => scrollToSection(item.section)}
                  sx={{
                    minWidth: 180,
                    '&:hover': {
                      bgcolor: 'rgba(89, 208, 255, 0.14)',
                      color: 'white',
                    },
                  }}
                >
                  <Typography textAlign="center">{item.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
